/**
 * Browser-side Graph operations for admin disposition.
 *
 * Uses raw fetch with bearer tokens from MSAL (not the @microsoft/microsoft-graph-client
 * package, which pulls Node polyfills into the bundle). Each request is a single
 * round-trip; failures bubble up so the UI can report per-asset progress.
 */
import { getToken } from "./auth.js";
import { ADMIN_CONFIG } from "./config.js";

const GRAPH = "https://graph.microsoft.com/v1.0";

interface DriveItemRef {
  driveItemId: string;
  listItemId: string;
  driveId: string;
  filename: string;
  variantWidth: number;
  albumPath: string;
  downloadUrl?: string;
}

interface SiteDriveCache {
  publicListId?: string;
  publicDriveId?: string;
  privateDriveId?: string;
}

const cache: SiteDriveCache = {};

async function graph<T>(path: string, init?: RequestInit): Promise<T> {
  const token = await getToken();
  const url = path.startsWith("http") ? path : `${GRAPH}${path}`;
  const res = await fetch(url, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Graph ${res.status} ${res.statusText} — ${path} — ${text.slice(0, 500)}`);
  }
  if (res.status === 204) return undefined as unknown as T;
  return (await res.json()) as T;
}

async function resolvePublicList(): Promise<string> {
  if (cache.publicListId) return cache.publicListId;
  const res = await graph<{ value: Array<{ id: string; displayName: string }> }>(
    `/sites/${ADMIN_CONFIG.siteId}/lists?$select=id,displayName`,
  );
  const hit = res.value.find((l) => l.displayName === ADMIN_CONFIG.publicLibrary);
  if (!hit) throw new Error(`List "${ADMIN_CONFIG.publicLibrary}" not found on site`);
  cache.publicListId = hit.id;
  return hit.id;
}

async function resolveDriveIds(): Promise<{ publicDriveId: string; privateDriveId: string }> {
  if (cache.publicDriveId && cache.privateDriveId) {
    return { publicDriveId: cache.publicDriveId, privateDriveId: cache.privateDriveId };
  }
  // Public + private libraries now live on DIFFERENT sites (private is isolated).
  const [pubRes, privRes] = await Promise.all([
    graph<{ value: Array<{ id: string; name: string }> }>(`/sites/${ADMIN_CONFIG.siteId}/drives?$select=id,name`),
    graph<{ value: Array<{ id: string; name: string }> }>(`/sites/${ADMIN_CONFIG.privateSiteId}/drives?$select=id,name`),
  ]);
  const pub = pubRes.value.find((d) => d.name === ADMIN_CONFIG.publicLibrary);
  const priv = privRes.value.find((d) => d.name === ADMIN_CONFIG.privateLibrary);
  if (!pub) throw new Error(`Drive "${ADMIN_CONFIG.publicLibrary}" not found`);
  if (!priv) throw new Error(`Drive "${ADMIN_CONFIG.privateLibrary}" not found on private site`);
  cache.publicDriveId = pub.id;
  cache.privateDriveId = priv.id;
  return { publicDriveId: pub.id, privateDriveId: priv.id };
}

/** Find every public-library item (original + variants) for a given asset id. */
export async function findVariantsForAsset(assetId: string): Promise<DriveItemRef[]> {
  const [listId, { publicDriveId }] = await Promise.all([resolvePublicList(), resolveDriveIds()]);

  // $filter on a plain text custom column. Requires the Prefer header because
  // ParentAssetId isn't an indexed column in this tenant.
  const items = await graph<{ value: ListItemRaw[] }>(
    `/sites/${ADMIN_CONFIG.siteId}/lists/${listId}/items?$expand=driveItem&$filter=fields/ParentAssetId eq '${encodeURIComponent(assetId)}'`,
    { headers: { Prefer: "HonorNonIndexedQueriesWarningMayFailRandomly" } },
  );

  // The $filter response doesn't include `fields` by default when you also
  // $expand=driveItem (Graph quirk). Re-fetch each item with fields if we
  // got hits — typically 8–10 variants per asset, so the extra calls are cheap.
  const refs: DriveItemRef[] = [];
  for (const it of items.value) {
    const full = await graph<ListItemRaw>(
      `/sites/${ADMIN_CONFIG.siteId}/lists/${listId}/items/${it.id}?$expand=fields,driveItem`,
    );
    if (!full.driveItem?.id) continue;
    refs.push({
      driveItemId: full.driveItem.id,
      listItemId: full.id,
      driveId: publicDriveId,
      filename: full.fields?.FileLeafRef ?? full.driveItem.name ?? "",
      variantWidth: full.fields?.VariantWidth ?? 0,
      albumPath: full.fields?.AlbumPath ?? "",
      downloadUrl: full.driveItem["@microsoft.graph.downloadUrl"],
    });
  }
  return refs;
}

/**
 * Move an asset's original file to InternalAssets, then delete every public
 * variant (original + resized copies). Returns the new private driveItem id.
 */
export async function moveAssetToInternal(
  assetId: string,
  variants: DriveItemRef[],
): Promise<{ newPrivateItemId: string; deletedCount: number }> {
  const { privateDriveId } = await resolveDriveIds();

  const original = pickOriginal(variants);
  if (!original) throw new Error(`No original (VariantWidth=0) found for asset ${assetId}`);
  if (!original.downloadUrl) {
    throw new Error(`Original for ${assetId} has no downloadUrl — was it just modified?`);
  }

  // 1. Download original into the browser.
  const blob = await fetch(original.downloadUrl).then(async (r) => {
    if (!r.ok) throw new Error(`Download failed ${r.status}: ${original.filename}`);
    return r.arrayBuffer();
  });

  // 2. Upload to InternalAssets at the same album path. ensureFolder isn't
  // needed — the PUT-by-path endpoint auto-creates parent folders.
  const targetPath = original.albumPath
    ? `${original.albumPath.replace(/^\/+|\/+$/g, "")}/${original.filename}`
    : original.filename;

  const uploadEndpoint = `/drives/${privateDriveId}/root:/${encodePath(targetPath)}:/content`;
  const token = await getToken();
  const uploadRes = await fetch(`${GRAPH}${uploadEndpoint}`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/octet-stream",
    },
    body: blob,
  });
  if (!uploadRes.ok) {
    const text = await uploadRes.text();
    throw new Error(`Upload to InternalAssets failed ${uploadRes.status}: ${text.slice(0, 300)}`);
  }
  const newItem = (await uploadRes.json()) as { id: string };

  // 3. Delete every public-library item for this asset (original + variants).
  // Doing this sequentially keeps the failure mode simple: if one delete 404s
  // it's already gone, treat as success and continue.
  let deletedCount = 0;
  for (const v of variants) {
    try {
      await graph<void>(`/drives/${v.driveId}/items/${v.driveItemId}`, { method: "DELETE" });
      deletedCount++;
    } catch (e) {
      const msg = (e as Error).message;
      if (msg.includes("404")) {
        deletedCount++; // already gone, count it
      } else {
        throw e;
      }
    }
  }

  return { newPrivateItemId: newItem.id, deletedCount };
}

/** Read existing blocklist.json (if any). */
export async function fetchBlocklist(): Promise<string[]> {
  const { publicDriveId } = await resolveDriveIds();
  try {
    const res = await graph<{ "@microsoft.graph.downloadUrl"?: string }>(
      `/drives/${publicDriveId}/root:/${ADMIN_CONFIG.blocklistFilename}`,
    );
    if (!res["@microsoft.graph.downloadUrl"]) return [];
    const json = await fetch(res["@microsoft.graph.downloadUrl"]).then((r) => r.json());
    return Array.isArray(json) ? json : (json.blockedIds ?? []);
  } catch (e) {
    if ((e as Error).message.includes("404")) return [];
    throw e;
  }
}

/** Write the updated blocklist back to SP. Replaces the file in place. */
export async function uploadBlocklist(blockedIds: string[]): Promise<void> {
  const { publicDriveId } = await resolveDriveIds();
  const payload = {
    updatedAt: new Date().toISOString(),
    blockedIds: [...new Set(blockedIds)].sort(),
  };
  const body = JSON.stringify(payload, null, 2);
  const token = await getToken();
  const res = await fetch(
    `${GRAPH}/drives/${publicDriveId}/root:/${ADMIN_CONFIG.blocklistFilename}:/content`,
    {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body,
    },
  );
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Blocklist upload failed ${res.status}: ${text.slice(0, 300)}`);
  }
}

function pickOriginal(variants: DriveItemRef[]): DriveItemRef | undefined {
  // Prefer the explicit passthrough (VariantWidth=0). Fall back to the largest
  // width if no passthrough exists (covers older Canto imports that didn't
  // get a VariantWidth=0 row).
  return (
    variants.find((v) => v.variantWidth === 0) ??
    [...variants].sort((a, b) => b.variantWidth - a.variantWidth)[0]
  );
}

function encodePath(p: string): string {
  return p
    .split("/")
    .map((seg) => encodeURIComponent(seg))
    .join("/");
}

interface ListItemRaw {
  id: string;
  fields?: {
    FileLeafRef?: string;
    VariantWidth?: number;
    AlbumPath?: string;
    ParentAssetId?: string;
    CantoId?: string;
  };
  driveItem?: {
    id?: string;
    name?: string;
    "@microsoft.graph.downloadUrl"?: string;
  };
}
